import React from 'react';
import { Trophy, Target, Clock, CheckCircle2, XCircle, BarChart3 } from 'lucide-react';

//  Helper Definitions re-defined for layout
const Card = ({ children, className = '' }) => (
  // Removed shadow and hover for PDF, kept border for structure clarity
  <div className={`bg-white rounded-2xl p-6 border border-gray-200 ${className}`}>
    {children}
  </div>
);

// Simplified metricCard component for stats
const MetricCard = ({ title, value, unit = '', icon: Icon, color = 'text-gray-800' }) => (
    <Card className="!p-4"> 
      <div className="flex flex-row items-center justify-start h-full w-full gap-3">
        {Icon && <Icon size={24} className={`${color}`} />}
        <div className="flex flex-col items-start justify-center">
          <h3 className="text-gray-500 text-xs font-semibold uppercase mb-0">{title}</h3>
          <p className="text-xl font-bold text-gray-800">{value}{unit}</p>
        </div>
      </div>
    </Card>
);

const formatSecondsToMinutesSeconds = (totalSeconds) => {
    if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '0m 0s';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    return `${minutes}m ${seconds}s`;
};

// determine score color
const getScoreColor = (score) => {
    if (score > 0) return 'text-green-500';
    if (score < 0) return 'text-red-500';
    return 'text-gray-500';
};

const PDFTrophyIcon = () => (
    // Uses text-center on a block element to center the large emoji
    <div className="text-center mx-auto mb-4" role="img" aria-label="Trophy">
        <span className="text-6xl text-yellow-500">🏆</span>
    </div>
);

  const getRankEmoji = (rank) => {
    switch(rank) {
      case 1: return "🥇";
      case 2: return "🥈";
      case 3: return "🥉";
      default: return "🏅";
    }
  };

// ----------------------------------------------------------------------
// Main PDF Layout Component
// ----------------------------------------------------------------------

const MultiplayerGamePDFLayout = ({ gameData, currentPlayer, getRankEmoji, userTotalScore, roomId }) => {
    
    // Ensure gameData is available before rendering
    if (!gameData) return <div className="text-center p-10">Loading game data for PDF...</div>;
    
    // The action button section is excluded from the JSX structure copied from MultiplayerGameReport.js
    return (
        // outer div is adjusted for PDF layout
        <div className="bg-white p-6 w-full font-zonaProRegular">

            <div className="relative z-10" id="multiplayer-report-content">
                {/* Header */}
                <div className="relative mb-8 h-16">
        
                    {/* Title Container: Centered using flex */}
                    <div className="flex justify-center items-center h-full">
                        <h1 className="text-4xl font-bold font-zonaProRegular text-cyan-500">
                            Multiplayer Game Report
                        </h1>
                    </div>
                    
                    {/* Total Points */}
                    <div className="absolute right-0 top-0 text-right">
                        <span className="text-gray-500 text-sm uppercase tracking-wider">Total Points</span>
                        <p 
                            className="text-2xl font-bold" 
                            // Inline style for green color rendering
                            style={{ color: '#10B981' }} 
                        >
                            {userTotalScore !== null ? userTotalScore : (currentPlayer?.totalScore || 0)}
                        </p>
                    </div>
                </div>

                {/* Room Info Header */}
                <section className="mb-8">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-8">
                            <div className="flex items-center space-x-2">
                                <span className="text-lg font-semibold text-gray-600">Room Code:</span>
                                <span className="bg-gray-100 px-3 py-1 rounded-lg font-mono text-lg font-bold font-zonaProRegular text-gray-800">
                                    {gameData?.roomId || roomId}
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="text-lg font-semibold text-gray-600">Room Name:</span>
                                <span className="text-lg font-bold font-zonaProRegular text-gray-800">
                                    {gameData?.gameInfo?.roomName || 'Challenge'}
                                </span>
                            </div>
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold font-zonaProRegular text-gray-800">{gameData?.gameInfo?.subject || 'Game Report'}</h2>
                        </div>
                    </div>
                </section>

                {/* Game Info Section */}
                <section className="mb-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Right Side: Scoreboard Podium */}
                        <Card>
                            <h3 className="text-xl font-semibold text-center text-gray-700 mb-6">Leaderboard</h3>

                            {/* Dynamic layout based on number of players */}
                            {gameData.players.length <= 3 ? (
                                /* Show only podium for 3 or fewer players */
                                <div className="flex items-center justify-center min-h-[300px] sm:min-h-[350px] md:min-h-[400px]">
                                    <div className="flex items-end justify-center space-x-2 sm:space-x-4 md:space-x-6 lg:space-x-8">
                                        {/* 2nd Place */}
                                        {gameData.players.find(p => p.rank === 2) && (
                                            <div className="flex flex-col items-center">
                                                <div className="bg-gray-200 rounded-t-3xl py-2 px-1 sm:py-3 sm:px-2 md:py-4 md:px-3 w-16 h-14 sm:w-20 sm:h-16 md:w-28 md:h-24 flex flex-col items-center justify-center shadow-lg font-zonaProRegular gap-1">
                                                    <div className="text-2xl sm:text-3xl md:text-4xl">🥈</div>
                                                    <div className="text-xs sm:text-sm font-bold text-gray-600">2nd</div>
                                                </div>
                                                <div className="text-center mt-2 sm:mt-3 md:mt-4">
                                                    <div className="font-bold text-xs sm:text-sm md:text-base font-zonaProRegular text-gray-800 mb-1">{gameData.players.find(p => p.rank === 2)?.name}</div>
                                                    <div className="text-xs sm:text-sm text-gray-600">{gameData.players.find(p => p.rank === 2)?.totalScore} pts</div>
                                                </div>
                                            </div>
                                        )}

                                        {/* 1st Place */}
                                        {gameData.players.find(p => p.rank === 1) && (
                                            <div className="flex flex-col items-center">
                                                <div className="bg-yellow-200 rounded-t-3xl py-3 px-2 sm:py-4 sm:px-3 md:py-5 md:px-4 w-20 h-18 sm:w-24 sm:h-20 md:w-32 md:h-32 flex flex-col items-center justify-center shadow-lg gap-2">
                                                    <div className="text-3xl sm:text-4xl md:text-5xl">🥇</div>
                                                    <div className="text-xs sm:text-sm md:text-base font-bold text-yellow-700">1st</div>
                                                </div>
                                                <div className="text-center mt-2 sm:mt-3 md:mt-4">
                                                    <div className="font-bold text-xs sm:text-sm md:text-base font-zonaProRegular text-gray-800 mb-1">{gameData.players.find(p => p.rank === 1)?.name}</div>
                                                    <div className="text-xs sm:text-sm text-gray-600">{gameData.players.find(p => p.rank === 1)?.totalScore} pts</div>
                                                </div>
                                            </div>
                                        )}

                                        {/* 3rd Place */}
                                        {gameData.players.find(p => p.rank === 3) && (
                                            <div className="flex flex-col items-center">
                                                <div className="bg-orange-200 rounded-t-3xl py-1 px-1 sm:py-2 sm:px-2 md:py-3 md:px-3 w-16 h-12 sm:w-20 sm:h-14 md:w-28 md:h-20 flex flex-col items-center justify-center shadow-lg gap-1">
                                                    <div className="text-xl sm:text-2xl md:text-3xl">🥉</div>
                                                    <div className="text-xs sm:text-sm font-bold text-orange-700">3rd</div>
                                                </div>
                                                <div className="text-center mt-2 sm:mt-3 md:mt-4">
                                                    <div className="font-bold text-xs sm:text-sm md:text-base text-gray-800 mb-1">{gameData.players.find(p => p.rank === 3)?.name}</div>
                                                    <div className="text-xs sm:text-sm text-gray-600">{gameData.players.find(p => p.rank === 3)?.totalScore} pts</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                /* Show podium + remaining players for more than 3 players */
                                <div className="flex flex-col lg:flex-row gap-8 items-start mt-4">
                                    {/* Podium for Top 3 */}
                                    <div className="w-full lg:w-2/3 flex items-center justify-center min-h-[300px] sm:min-h-[350px] md:min-h-[400px]">
                                        <div className="flex items-end justify-center space-x-2 sm:space-x-4 md:space-x-6 lg:space-x-8">
                                            {/* 2nd Place */}
                                            {gameData.players.find(p => p.rank === 2) && (
                                                <div className="flex flex-col items-center">
                                                    <div className="bg-gray-200 rounded-t-3xl py-2 px-1 sm:py-3 sm:px-2 md:py-4 md:px-3 w-16 h-14 sm:w-20 sm:h-16 md:w-28 md:h-24 flex flex-col items-center justify-center shadow-lg font-zonaProRegular gap-1">
                                                        <div className="text-2xl sm:text-3xl md:text-4xl">🥈</div>
                                                        <div className="text-xs sm:text-sm font-bold text-gray-600">2nd</div>
                                                    </div>
                                                    <div className="text-center mt-2 sm:mt-3 md:mt-4">
                                                        <div className="font-bold text-xs sm:text-sm md:text-base font-zonaProRegular text-gray-800 mb-1">{gameData.players.find(p => p.rank === 2)?.name}</div>
                                                        <div className="text-xs sm:text-sm text-gray-600">{gameData.players.find(p => p.rank === 2)?.totalScore} pts</div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* 1st Place */}
                                            {gameData.players.find(p => p.rank === 1) && (
                                                <div className="flex flex-col items-center">
                                                    <div className="bg-yellow-200 rounded-t-3xl py-3 px-2 sm:py-4 sm:px-3 md:py-5 md:px-4 w-20 h-18 sm:w-24 sm:h-20 md:w-32 md:h-32 flex flex-col items-center justify-center shadow-lg gap-2">
                                                        <div className="text-3xl sm:text-4xl md:text-5xl">🥇</div>
                                                        <div className="text-xs sm:text-sm md:text-base font-bold text-yellow-700">1st</div>
                                                    </div>
                                                    <div className="text-center mt-2 sm:mt-3 md:mt-4">
                                                        <div className="font-bold text-xs sm:text-sm md:text-base font-zonaProRegular text-gray-800 mb-1">{gameData.players.find(p => p.rank === 1)?.name}</div>
                                                        <div className="text-xs sm:text-sm text-gray-600">{gameData.players.find(p => p.rank === 1)?.totalScore} pts</div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* 3rd Place */}
                                            {gameData.players.find(p => p.rank === 3) && (
                                                <div className="flex flex-col items-center">
                                                    <div className="bg-orange-200 rounded-t-3xl py-1 px-1 sm:py-2 sm:px-2 md:py-3 md:px-3 w-16 h-12 sm:w-20 sm:h-14 md:w-28 md:h-20 flex flex-col items-center justify-center shadow-lg gap-1">
                                                        <div className="text-xl sm:text-2xl md:text-3xl">🥉</div>
                                                        <div className="text-xs sm:text-sm font-bold text-orange-700">3rd</div>
                                                    </div>
                                                    <div className="text-center mt-2 sm:mt-3 md:mt-4">
                                                        <div className="font-bold text-xs sm:text-sm md:text-base text-gray-800 mb-1">{gameData.players.find(p => p.rank === 3)?.name}</div>
                                                        <div className="text-xs sm:text-sm text-gray-600">{gameData.players.find(p => p.rank === 3)?.totalScore} pts</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Rest of the players */}
                                    <div className="w-full lg:w-1/3">
                                        <h4 className="text-lg font-semibold text-gray-700 mb-4">Other Players</h4>
                                        <div className="space-y-2">
                                            {gameData.players
                                                .filter(p => p.rank > 3)
                                                .sort((a, b) => a.rank - b.rank)
                                                .map((player) => (
                                                    <div key={player.userId} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="bg-gray-200 text-gray-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                                                                {player.rank}
                                                            </div>
                                                            <div>
                                                                <div className="font-semibold text-sm text-gray-800">{player.name}</div>
                                                                <div className="text-xs text-gray-500">Rank #{player.rank}</div>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="font-bold text-sm text-gray-800">{player.totalScore}</div>
                                                            <div className="text-xs text-gray-500">points</div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card>


                        {/* Player Stats Card */}
                        {currentPlayer ? (
                            <Card>
                                <div className="text-center mb-6">
                                    <div className="text-center mb-4">
                                        <div className="print:block text-6xl text-yellow-500" role="img" aria-label="Trophy">🏆</div>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                                        {getRankEmoji(currentPlayer.rank)} Rank #{currentPlayer.rank}
                                    </h3>
                                    <div className="text-2xl font-bold text-cyan-500 mb-2">
                                        {currentPlayer.name}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {currentPlayer.isWinner ? "🏆 CHAMPION! 🏆" : `Final Score: ${currentPlayer.totalScore} points`}
                                    </div>
                                </div>

                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 gap-4">
                                    <MetricCard
                                        title="Total Score"
                                        value={currentPlayer.totalScore}
                                        icon={BarChart3}
                                        color="text-green-500"
                                    />
                                    <MetricCard
                                        title="Accuracy"
                                        value={currentPlayer.accuracy}
                                        unit="%"
                                        icon={Target}
                                        color="text-blue-500"
                                    />
                                    <MetricCard
                                        title="Correct Answers"
                                        value={`${currentPlayer.correctAnswers}/${currentPlayer.totalAnswers}`}
                                        icon={CheckCircle2}
                                        color="text-purple-500"
                                    />
                                    <MetricCard
                                        title="Time Spent"
                                        value={formatSecondsToMinutesSeconds(gameData.gameInfo.duration)}
                                        icon={Clock}
                                        color="text-orange-500"
                                    />
                                </div>
                            </Card>
                        ) : (
                            <Card>
                                <div className="text-center py-8">
                                    <div className="text-gray-500 text-lg">
                                        Player data not available
                                    </div>
                                </div>
                            </Card>
                        )}
                    </div>
                </section>

                {/* Question Details Section */}
                <section className="mb-10">
                    <Card>
                        <h3 className="text-lg font-semibold text-gray-700 mb-6 text-left">Question-by-Question Breakdown</h3>

                        <div className="space-y-6">
                            {gameData.questions.map((question, index) => (
                                // Added print-break-inside-avoid to try and keep question blocks from breaking across pages
                                <div key={question.questionId} className="bg-gray-50 rounded-2xl p-6 border-2 border-gray-100 hover:border-cyan-200 transition-all duration-300 print:break-inside-avoid">
                                    {/* Question Header */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center space-x-3">
                                            <div className="bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                                                <span className="mb-4">
                                                    {question.questionNumber}
                                                </span>
                                            </div>
                                            <div>
                                                <div className="font-semibold text-gray-800">{question.category} • {question.difficulty}</div>
                                                <div className="text-sm text-gray-500">Question #{question.questionNumber}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            {question.status === 'correct' ? (
                                                <CheckCircle2 size={24} className="text-green-500" />
                                            ) : (
                                                question.status === 'incorrect' ? (
                                                    <XCircle size={24} className="text-red-500" />
                                                ) : (
                                                    <XCircle size={24} className="text-yellow-500" />
                                                )
                                            )}
                                            <div className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(question.points)}`}>
                                                {question.points > 0 ? '+' : ''}{question.points} pts
                                            </div>
                                        </div>
                                    </div>

                                    <div className="md:flex md:gap-8">
                                        {/* Left Side: Question & Feedback */}
                                        <div className="md:w-3/5 lg:w-1/2 space-y-5">
                                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 font-zonaProUltra mb-1">Question:</h1>
                                            <p className="text-lg md:text-xl text-gray-700 text-left leading-relaxed whitespace-pre-line">
                                                {question.questionText}
                                            </p>
                                            <div className={`mt-6 p-5 rounded-lg border-l-4 ${question.status === 'correct' ? 'border-green-500' : question.status === 'incorrect' ? 'border-red-500' : 'border-yellow-500'}`}>
                                                <h3 className={`text-xl font-semibold mb-2 font-zonaProUltra ${question.status === 'correct' ? 'text-green-700' : question.status === 'incorrect' ? 'text-red-700' : 'text-yellow-700'}`}>
                                                    {question.status === 'correct' ? 'CORRECT!' : question.status === 'incorrect' ? 'INCORRECT!' : 'UNANSWERED!'}
                                                </h3>
                                                <p className={`mb-2 ${question.status === 'correct' ? 'text-green-700' : question.status === 'incorrect' ? 'text-red-700' : 'text-yellow-700'}`}>
                                                    {question.feedback ? question.feedback : 'Great job on this question!'}
                                                </p>
                                                <div className={`text-lg font-semibold ${question.status === 'correct' ? 'text-green-700' : question.status === 'incorrect' ? 'text-red-700' : 'text-yellow-700'}`}>
                                                    Points: <span className="font-bold">{question.points > 0 ? `+${question.points}` : question.points}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Side: Answer Options */}
                                        <div className="md:w-2/5 lg:w-1/2 md:pl-4 lg:pl-6 mt-8 md:mt-0">
                                            <h2 className="text-lg sm:text-xl font-zonaProUltra text-gray-700 mb-4">
                                                Results:
                                            </h2>
                                            {question.questionType === 'Grid-in' ? (
                                                <div className="space-y-3">
                                                    {question.isCorrect && (
                                                        <div className="w-full text-left p-4 rounded-lg border-2 font-medium bg-green-50 border-green-500 text-green-700">
                                                            <span className="font-bold mr-2">Your Answer:</span>{question.userAnswer}
                                                        </div>
                                                    )}

                                                    {!question.isCorrect && question.userAnswer && (
                                                        <>
                                                            <div className="w-full text-left p-4 rounded-lg border-2 font-medium bg-red-50 border-red-500 text-red-700">
                                                                <span className="font-bold mr-2">Your Answer:</span>{question.userAnswer}
                                                            </div>
                                                            <div className="w-full text-left p-4 rounded-lg border-2 font-medium bg-green-50 border-green-500 text-green-700">
                                                                <span className="font-bold mr-2">Correct Answer:</span>{question.correctAnswer}
                                                            </div>
                                                        </>
                                                    )}

                                                    {!question.userAnswer && (
                                                        <div className="w-full text-left p-4 rounded-lg border-2 font-medium bg-green-50 border-green-500 text-green-700">
                                                            <span className="font-bold mr-2">Correct Answer:</span>{question.correctAnswer}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {question.options && Object.entries(question.options).map(([key, value]) => {
                                                        const isUserAnswer = question.userAnswer === value;
                                                        const isCorrectAnswer = question.correctAnswer === value;

                                                        let classes = "w-full text-left p-4 rounded-lg border-2 font-medium";

                                                        if (isUserAnswer && !isCorrectAnswer) {
                                                            classes += ' bg-red-50 border-red-400 text-red-700'; // User's incorrect choice
                                                        } else if (isCorrectAnswer) {
                                                            classes += ' bg-green-50 border-green-500 text-green-700'; // The correct answer
                                                        } else {
                                                            classes += ' bg-gray-100 border-gray-300 text-gray-500 opacity-75'; // Other options
                                                        }

                                                        return (
                                                            <div key={key} className={classes}>
                                                                <span className="font-bold mr-2">{key})</span>{value}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Stats Row */}
                                    <div className="flex flex-wrap items-center justify-between text-sm text-gray-600 bg-white rounded-xl p-3 mt-4 border border-gray-100">
                                        <div className="flex items-center space-x-4">
                                            <div className="flex items-center space-x-1">
                                                <Clock size={16} />
                                                <span>{question.timeSpent}s</span>
                                            </div>
                                        </div>
                                        <div className={`font-bold text-lg ${getScoreColor(question.points)}`}>
                                            {question.points > 0 ? '+' : ''}{question.points} points
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </section>

                {/* Action Buttons Section intentionally excluded from the PDF layout */}
            </div>
        </div>
    );
};

export default MultiplayerGamePDFLayout;